import { Button, Intent, Tag } from '@blueprintjs/core';
import { M1_DEFAULT } from '@retro-ui/shared';
import cx from 'classnames';
import { CallHistoryMethodAction, push as changeLocation } from 'connected-react-router';
import { Path } from 'history';
import _ from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router-dom';

import Select from '@f/components/shared/select/Select';
import Table from '@f/components/shared/table/Table';
import Toolbar from '@f/components/shared/toolbar/Toolbar';
import {
  findReactionSearchPreferences,
  patchReactionSearchPreferences,
  removeReactionSearchPreferences,
} from '@f/services/reaction-search-preference/reaction-search-preference.actions';

import { Preset } from '../preset/Preset';

import cls from './PresetList.module.scss';

interface Props extends PropsFromRedux, RouteComponentProps {}

const buildTableSchema = (onRemove: (items: string[]) => void) => [
  {
    label: 'NAME',
    renderer: (item: Preset) => item.name,
  },
  {
    label: '',
    grow: true,
    renderer: (item: Preset) =>
      item.isApiDefault ? (
        <Tag
          className={cx(cls.defaultIndicator, cls.defaultApi)}
          round
          key={`iad-${item.id}`}
          data-test="api-default"
        >
          Default for search
        </Tag>
      ) : null,
  },
  {
    label: '',
    renderer: (item: Preset) => {
      const restricted = item.name === M1_DEFAULT;
      return !restricted ? (
        <Button
          key={item.id}
          small
          minimal
          icon="trash"
          onClick={(ev: React.MouseEvent<HTMLElement, MouseEvent>) => {
            ev.stopPropagation();
            onRemove([item.id]);
          }}
        />
      ) : null;
    },
  },
];

function PresetList({
  reactionSearchPreferences,
  isProcessing,
  errors,
  match,
  changeLocation,
  findReactionSearchPreferences,
  removeReactionSearchPreferences,
}: Props) {
  useEffect(() => {
    findReactionSearchPreferences();
  }, []);

  const onCreate = useCallback(() => {
    const url = `${match.path}/new`;
    changeLocation(url);
  }, [match.path, changeLocation]);

  const onRowClick = (item: Preset) => {
    const url = `${match.path}/${item.id}/edit`;
    changeLocation(url);
  };

  const tableSchema = buildTableSchema(removeReactionSearchPreferences);

  return (
    <div className={cls.page} data-test="reaction-search-parameters-list-page">
      <Table
        className={cls.table}
        schema={tableSchema}
        items={reactionSearchPreferences}
        interactive
        // tableProps={{ striped: false }}
        loading={isProcessing}
        loadingCaption="Loading presets..."
        emptyStateProps={{
          title: 'No presets',
          description: "You haven't defined any preset yet.",
          action: (
            <Button
              large
              intent={Intent.PRIMARY}
              disabled={isProcessing}
              text="New preset"
              icon="add"
              onClick={onCreate}
            />
          ),
        }}
        onRowClick={onRowClick}
      />
    </div>
  );
}

function ToolbarContext(props: Props) {
  const {
    match,
    isProcessing,
    reactionSearchPreferences,
    m1DefaultPresetId,
    changeLocation,
    patchReactionSearchPreferences,
    findReactionSearchPreferences,
  } = props;

  const onCreate = useCallback(() => {
    const url = `${match.path}/new`;
    changeLocation(url);
  }, [match.path, changeLocation]);

  const apiDefaultId = reactionSearchPreferences.find(it => it.isApiDefault)?.id;

  const onApiDefaultChange = async (item: Preset): Promise<void> => {
    await patchReactionSearchPreferences({
      id: item ? item.id : m1DefaultPresetId,
      isApiDefault: true,
    } as Preset);
    findReactionSearchPreferences();
  };

  return (
    <>
      <Toolbar.Item className={cls.defaultSelection} light>
        <span>Default for search</span>
        <Select
          large
          items={reactionSearchPreferences}
          activeItemId={apiDefaultId}
          idField="id"
          labelField="name"
          allowNoSelection
          filterable={false}
          className={cx(cls.presetSelect, cls.apiSelect)}
          placeholder="(System default)"
          rightIcon="caret-down"
          onChange={onApiDefaultChange}
        />
      </Toolbar.Item>

      <Toolbar.Item alignRight>
        <Button
          large
          intent={Intent.PRIMARY}
          disabled={isProcessing}
          text="New preset"
          icon="add"
          onClick={onCreate}
        />
      </Toolbar.Item>
    </>
  );
}

interface RootState {
  reactionSearchPreference: { data: Preset[]; find: { processing: number; error: any } };
  m1DefaultPresetId: string;
  administration: {
    debug: {
      isDebugModeEnabled: boolean;
    };
  };
}

interface Dispatch {
  findReactionSearchPreferences: () => void;
  patchReactionSearchPreferences: (
    preset: Preset
  ) => (dispatch: any, getState: any, thunkExtraArgument: any) => Promise<void>;
  removeReactionSearchPreferences: (ids: string[]) => void;
  changeLocation<LocationState>(
    path: Path,
    state?: LocationState
  ): CallHistoryMethodAction<[Path, LocationState?]>;
}

const filterAndSortPreferences = (data: Preset[], isDebug: boolean): Preset[] => {
  return _.chain(data)
    .filter((item: Preset) => (isDebug ? true : item.name !== M1_DEFAULT))
    .sortBy(['name'])
    .value();
};

const mapStateToProps = (state: RootState) => {
  const data = Object.values(state.reactionSearchPreference.data);
  const isDebugModeOn = state.administration.debug.isDebugModeEnabled;
  const reactionSearchPreferences = filterAndSortPreferences(data, isDebugModeOn);
  const m1DefaultPresetId = _.find(data, ['name', M1_DEFAULT])?.id;

  return {
    reactionSearchPreferences,
    m1DefaultPresetId,
    isProcessing: state.reactionSearchPreference.find.processing > 0,
    errors: [state.reactionSearchPreference.find.error],
  };
};

const dispatchToProps: Dispatch = {
  findReactionSearchPreferences,
  patchReactionSearchPreferences,
  removeReactionSearchPreferences,
  changeLocation,
};

const connector = connect(mapStateToProps, dispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

PresetList.ToolbarContext = withRouter(connector(ToolbarContext));

export default withRouter(connector(PresetList));
